import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Building2, User } from "lucide-react";
import { toast } from "sonner";

const emptyContact = { name: "", function: "", phone: "", email: "", branch_id: "" };
const emptyBranch = {
  name: "", address: "", postal_code: "", city: "",
  phone: "", email: "", siret: "", tva_number: "", website: "",
  is_headquarters: false, notes: "",
};

const NO_BRANCH = "__none__";
const branchLabel = (b, fallback = "Agence sans nom") => b?.name?.trim() || fallback;

export const SupplierFormDialog = ({ open, onClose, supplier, onSaved }) => {
  const editing = !!supplier;
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    company_name: "", address: "", phone: "", email: "", website: "",
    siret: "", tva_number: "", notes: "", lead_time_days: "", category_id: "",
    contacts: [], branches: [],
  });

  useEffect(() => {
    if (!open) return;
    api.getSupplierCategories()
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
    if (supplier) {
      setForm({
        company_name: supplier.company_name || "",
        address: supplier.address || "",
        phone: supplier.phone || "",
        email: supplier.email || "",
        website: supplier.website || "",
        siret: supplier.siret || "",
        tva_number: supplier.tva_number || "",
        notes: supplier.notes || "",
        lead_time_days: supplier.lead_time_days ?? "",
        category_id: supplier.category_id || "",
        contacts: (supplier.contacts || []).map((c) => ({ ...c, branch_id: c.branch_id || "" })),
        branches: supplier.branches || [],
      });
    } else {
      setForm({
        company_name: "", address: "", phone: "", email: "", website: "",
        siret: "", tva_number: "", notes: "", lead_time_days: "", category_id: "",
        contacts: [], branches: [],
      });
    }
  }, [open, supplier]);

  const updateContact = (idx, key, value) => {
    const arr = [...form.contacts];
    arr[idx] = { ...arr[idx], [key]: value };
    setForm({ ...form, contacts: arr });
  };
  const removeContact = (idx) => {
    setForm({ ...form, contacts: form.contacts.filter((_, i) => i !== idx) });
  };

  const updateBranch = (idx, key, value) => {
    const arr = [...form.branches];
    // Only one HQ at a time
    if (key === "is_headquarters" && value) {
      arr.forEach((b, i) => { if (i !== idx) b.is_headquarters = false; });
    }
    arr[idx] = { ...arr[idx], [key]: value };
    setForm({ ...form, branches: arr });
  };
  const addBranch = () => {
    // Assign a client-side UUID immediately so newly-added branches can be
    // selected as `branch_id` on contacts before the supplier is saved.
    // The backend accepts stable ids and preserves them via _normalize_branches.
    const clientId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setForm({ ...form, branches: [...form.branches, { ...emptyBranch, id: clientId }] });
  };
  const removeBranch = (idx) => {
    const removed = form.branches[idx];
    const branches = form.branches.filter((_, i) => i !== idx);
    // Detach contacts pointing to the removed branch (client-side ; server double-checks)
    const contacts = form.contacts.map((c) =>
      c.branch_id && removed?.id && c.branch_id === removed.id ? { ...c, branch_id: "" } : c
    );
    setForm({ ...form, branches, contacts });
  };

  const handleSubmit = async () => {
    if (!form.company_name.trim()) {
      toast.error("Nom de l'entreprise requis");
      return;
    }
    // Basic branch validation : chaque agence doit avoir un nom
    for (const [i, b] of form.branches.entries()) {
      if (!b.name?.trim()) {
        toast.error(`Agence #${i + 1} : nom obligatoire`);
        return;
      }
    }
    try {
      const payload = {
        ...form,
        lead_time_days: form.lead_time_days === "" ? null : Number(form.lead_time_days),
        category_id: form.category_id || null,
        contacts: form.contacts.map((c) => ({ ...c, branch_id: c.branch_id || null })),
      };
      if (editing) {
        await api.updateSupplier(supplier.id, payload);
        toast.success("Fournisseur mis à jour");
      } else {
        await api.createSupplier(payload);
        toast.success("Fournisseur créé");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le fournisseur" : "Nouveau fournisseur"}</DialogTitle>
          <DialogDescription>Coordonnées, agences et personnes à contacter.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {/* --- Identification principale --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nom de l&apos;entreprise *</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="supplier-company-name" />
            </div>
            <div className="md:col-span-2">
              <Label>Adresse (siège / par défaut)</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">Utilisez la section « Agences » ci-dessous pour renseigner les autres implantations.</p>
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Site web</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
            <div>
              <Label>SIRET</Label>
              <Input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />
            </div>
            <div>
              <Label>N° TVA intracommunautaire</Label>
              <Input value={form.tva_number} onChange={(e) => setForm({ ...form, tva_number: e.target.value })} />
            </div>
            <div>
              <Label>Délai de réapprovisionnement (jours)</Label>
              <Input
                type="number" min="0" max="365"
                value={form.lead_time_days}
                onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })}
                placeholder="Ex : 7"
                data-testid="supplier-lead-time"
              />
              <p className="text-xs text-muted-foreground mt-1">Durée typique entre commande et livraison. Sert à calculer la date limite de commande dans le prévisionnel de stock.</p>
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select
                value={form.category_id || NO_BRANCH}
                onValueChange={(v) => setForm({ ...form, category_id: v === NO_BRANCH ? "" : v })}
              >
                <SelectTrigger data-testid="supplier-category-select"><SelectValue placeholder="Sans catégorie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_BRANCH}>— Sans catégorie —</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`supplier-category-option-${c.id}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Gérez les catégories depuis Paramètres → Catégories fournisseurs.</p>
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          {/* --- Agences --- */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Agences ({form.branches.length})
              </Label>
              <Button size="sm" variant="outline" onClick={addBranch} data-testid="add-branch-btn">
                <Plus className="h-4 w-4 mr-1" /> Ajouter une agence
              </Button>
            </div>
            {form.branches.length === 0 && (
              <p className="text-xs italic text-muted-foreground">
                Aucune agence enregistrée. Le fournisseur n&apos;a qu&apos;une seule implantation (siège).
              </p>
            )}
            {form.branches.map((b, idx) => (
              <Card key={idx} data-testid={`branch-${idx}`} className="border-slate-200">
                <CardContent className="pt-3 pb-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={b.name}
                        onChange={(e) => updateBranch(idx, "name", e.target.value)}
                        placeholder="Ex : Agence Paris Nord, Dépôt Lyon..."
                        className="font-semibold"
                        data-testid={`branch-name-${idx}`}
                      />
                      {b.is_headquarters && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap">
                          Siège
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
                        <Checkbox
                          checked={!!b.is_headquarters}
                          onCheckedChange={(v) => updateBranch(idx, "is_headquarters", !!v)}
                          data-testid={`branch-hq-${idx}`}
                        />
                        Siège social
                      </label>
                      <Button variant="ghost" size="sm" onClick={() => removeBranch(idx)} data-testid={`remove-branch-${idx}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                    <div className="md:col-span-4">
                      <Label className="text-xs">Adresse</Label>
                      <Input value={b.address || ""} onChange={(e) => updateBranch(idx, "address", e.target.value)} data-testid={`branch-address-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Code postal</Label>
                      <Input value={b.postal_code || ""} onChange={(e) => updateBranch(idx, "postal_code", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Ville</Label>
                      <Input value={b.city || ""} onChange={(e) => updateBranch(idx, "city", e.target.value)} data-testid={`branch-city-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Téléphone</Label>
                      <Input value={b.phone || ""} onChange={(e) => updateBranch(idx, "phone", e.target.value)} data-testid={`branch-phone-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={b.email || ""} onChange={(e) => updateBranch(idx, "email", e.target.value)} data-testid={`branch-email-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Site web</Label>
                      <Input value={b.website || ""} onChange={(e) => updateBranch(idx, "website", e.target.value)} />
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">SIRET</Label>
                      <Input value={b.siret || ""} onChange={(e) => updateBranch(idx, "siret", e.target.value)} data-testid={`branch-siret-${idx}`} />
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">N° TVA intracommunautaire</Label>
                      <Input value={b.tva_number || ""} onChange={(e) => updateBranch(idx, "tva_number", e.target.value)} />
                    </div>
                    <div className="md:col-span-6">
                      <Label className="text-xs">Notes</Label>
                      <Input value={b.notes || ""} onChange={(e) => updateBranch(idx, "notes", e.target.value)} placeholder="Horaires, spécificités, contact préféré..." />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* --- Contacts --- */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base flex items-center gap-2">
                <User className="h-4 w-4" /> Personnes à contacter ({form.contacts.length})
              </Label>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, contacts: [...form.contacts, { ...emptyContact }] })} data-testid="add-contact-btn">
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </div>
            {form.contacts.length === 0 && <p className="text-xs italic text-muted-foreground">Aucune personne ajoutée.</p>}
            {form.contacts.map((c, idx) => (
              <Card key={idx} data-testid={`contact-${idx}`}>
                <CardContent className="pt-3 pb-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Nom *</Label>
                    <Input value={c.name} onChange={(e) => updateContact(idx, "name", e.target.value)} data-testid={`contact-name-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Fonction</Label>
                    <Input value={c.function || ""} onChange={(e) => updateContact(idx, "function", e.target.value)} placeholder="Commercial, ADV..." />
                  </div>
                  <div>
                    <Label className="text-xs">Téléphone</Label>
                    <Input value={c.phone || ""} onChange={(e) => updateContact(idx, "phone", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={c.email || ""} onChange={(e) => updateContact(idx, "email", e.target.value)} />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Agence</Label>
                      <Select
                        value={c.branch_id || NO_BRANCH}
                        onValueChange={(v) => updateContact(idx, "branch_id", v === NO_BRANCH ? "" : v)}
                      >
                        <SelectTrigger data-testid={`contact-branch-${idx}`}>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_BRANCH}>— Aucune —</SelectItem>
                          {form.branches
                            .filter((b) => b.id)
                            .map((b) => (
                              <SelectItem key={b.id} value={b.id} data-testid={`contact-branch-option-${idx}-${b.id}`}>
                                {branchLabel(b)}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeContact(idx)} data-testid={`remove-contact-${idx}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  {c.branch_id && !form.branches.find((b) => b.id === c.branch_id) && (
                    <p className="md:col-span-6 text-xs text-amber-600">
                      L&apos;agence rattachée à ce contact a été modifiée/supprimée : enregistrez le fournisseur pour synchroniser.
                    </p>
                  )}
                  {form.branches.length === 0 && (
                    <p className="md:col-span-6 text-xs italic text-muted-foreground">
                      Astuce : ajoutez d&apos;abord des agences pour pouvoir y rattacher ce contact.
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} data-testid="save-supplier-btn">{editing ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierFormDialog;
